import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { getMyEffectivePermissions } from "@/lib/api/endpoints/permissions";
import { setRouteFlash } from "@/lib/navigation/route-flash";
import { NO_ACCESS_ROUTE, resolveAccessibleRoute } from "@/lib/navigation/sidebar-nav";
import { acceptInvitation, verifyInvitationToken } from "@/modules/auth/api";
import { useAuthStore } from "@/modules/auth/store";

type VerificationState = "checking" | "valid" | "invalid";

export function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => searchParams.get("token")?.trim() ?? "", [searchParams]);
  const [verification, setVerification] = useState<VerificationState>("checking");
  const [name, setName] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const passwordsMatch = password === confirmPassword;
  const passwordReady = password.length >= 8 && confirmPassword.length >= 8;

  useEffect(() => {
    let cancelled = false;

    async function verify() {
      if (!token) {
        setVerification("invalid");
        return;
      }
      try {
        const result = await verifyInvitationToken(token);
        if (cancelled) return;
        setVerification(result.valid ? "valid" : "invalid");
        setName(result.name ?? null);
        setEmail(result.email ?? null);
        setExpiresAt(result.expires_at ?? null);
      } catch {
        if (!cancelled) setVerification("invalid");
      }
    }

    void verify();
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setSubmitted(true);
    if (password.length < 8) {
      setError("Use at least 8 characters for your password.");
      return;
    }
    if (!passwordsMatch) {
      setError("Password confirmation does not match.");
      return;
    }
    setPending(true);
    setError(null);
    try {
      const session = await acceptInvitation({
        token,
        password,
        confirm_password: confirmPassword,
      });
      useAuthStore.getState().setSession({ user: session.user, token: session.access_token });
      let destination = "/overview";
      try {
        const permissions = await getMyEffectivePermissions();
        const permissionMap = permissions.modules.reduce<Record<string, (typeof permissions.modules)[number]>>((acc, module) => {
          acc[module.module_key] = module;
          return acc;
        }, {});
        destination = resolveAccessibleRoute(session.user, permissionMap, "/overview");
      } catch {
        destination = resolveAccessibleRoute(session.user, undefined, "/overview");
      }
      if (destination === NO_ACCESS_ROUTE) {
        setRouteFlash("Sign-up successful. Your account is active, but an admin still needs to grant module access.");
      }
      navigate(destination, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not finish account setup.");
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="relative flex min-h-screen flex-col items-center justify-center bg-background p-6">
      <div className="relative w-full max-w-md rounded-card border border-card-border bg-card p-6 shadow-card backdrop-blur-xl md:p-8">
        <p className="text-micro font-semibold uppercase tracking-[0.14em] text-kicker-lux">Invitation</p>
        <h1 className="mt-2 font-sans text-2xl font-bold tracking-tight text-foreground">Set your password</h1>
        <p className="mt-2 text-caption text-muted">
          Finish your account setup to access the data logger workspace.
        </p>

        {verification === "checking" ? (
          <p className="mt-6 rounded-control border border-card-border bg-muted-bg px-3 py-2 text-caption text-muted">
            Checking invitation...
          </p>
        ) : null}

        {verification === "invalid" ? (
          <>
            <p role="alert" className="mt-6 rounded-control border border-error/25 bg-error/5 px-3 py-2 text-caption text-error">
              This invitation link is invalid or has expired.
            </p>
            <Link to="/login" className="mt-6 block text-center text-caption text-muted transition-colors hover:text-accent">
              Back to sign in
            </Link>
          </>
        ) : null}

        {verification === "valid" ? (
          <form className="mt-6 space-y-5" onSubmit={handleSubmit}>
            <div className="rounded-card border border-card-border bg-muted-bg px-4 py-3 text-sm text-foreground">
              <div><span className="font-semibold">Email:</span> {email ?? "-"}</div>
              <div><span className="font-semibold">Name:</span> {name ?? "-"}</div>
              {expiresAt ? <div><span className="font-semibold">Expires:</span> {new Date(expiresAt).toLocaleString()}</div> : null}
            </div>

            <label className="block space-y-2">
              <span className="dl-label text-[0.58rem] text-muted">Password</span>
              <input
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="ui-control w-full"
                autoComplete="new-password"
              />
              {submitted && password.length > 0 && password.length < 8 ? (
                <span className="text-caption text-error">Use at least 8 characters.</span>
              ) : null}
            </label>

            <label className="block space-y-2">
              <span className="dl-label text-[0.58rem] text-muted">Confirm password</span>
              <input
                type="password"
                required
                minLength={8}
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                className="ui-control w-full"
                autoComplete="new-password"
              />
              {submitted && confirmPassword.length > 0 && !passwordsMatch ? (
                <span className="text-caption text-error">Passwords do not match yet.</span>
              ) : null}
            </label>

            {error ? (
              <p role="alert" className="rounded-control border border-error/25 bg-error/5 px-3 py-2 text-caption text-error">{error}</p>
            ) : null}

            <button type="submit" disabled={pending || !passwordReady || !passwordsMatch} className="btn btn-primary w-full">
              {pending ? "Saving..." : "Activate account"}
            </button>
          </form>
        ) : null}
      </div>
    </main>
  );
}


